/**
 * Boardroom Socks — "Load More Socks" persistent-button pagination
 * Tracking doc: Section 23 (scope decision: Option A, persistent button, no auto-scroll)
 *
 * REWRITE NOTE: this version uses a single, document-level delegated click
 * listener instead of binding a listener to each button at page load. That
 * change was required because this theme's own filter sidebar (facets-vertical,
 * update_on_change: true) does its own AJAX swap of the entire collection
 * results section — including the Load More button and its container — every
 * time a filter changes. A listener bound directly to the original button
 * silently stopped working the instant the filter system replaced that node
 * with a freshly-rendered one. Delegation on document survives any number of
 * such swaps, since it never depends on the identity of a specific DOM node —
 * it just asks "was the clicked element a Load More button?" at click time.
 *
 * How it works:
 * - On click, reads data-next-url / data-section-id fresh off whichever
 *   [data-load-more-container] is currently in the DOM (via closest()), rather
 *   than caching them at page load.
 * - Fetches that URL with &section_id=<id> appended, so Shopify returns only
 *   that section's rendered HTML instead of a full page.
 * - Appends the new product cards to the grid and updates the count text and
 *   next-page URL in place.
 * - Hides the button once there's no further page.
 * - Deliberately does NOT touch window.location / history (matches Turbo's
 *   existing behavior — see Section 23).
 */
(function () {
  'use strict';

  // Progressive enhancement: only swap native pagination -> button once we
  // know JS actually ran. If this script fails to load/execute for any reason,
  // the native, fully-crawlable <a href="?page=2"> pagination stays visible
  // (see .js-load-more-active rules in theme.css) — no broken/empty state.
  document.documentElement.classList.add('js-load-more-active');

  let loading = false;

  function setLoadingState(button, isLoading) {
    button.disabled = isLoading;
    button.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      button.dataset.originalText = button.dataset.originalText || button.textContent;
      button.textContent = 'Loading…';
    } else if (button.dataset.originalText) {
      button.textContent = button.dataset.originalText;
    }
  }

  async function handleLoadMore(button) {
    if (loading) return;

    const container = button.closest('[data-load-more-container]');
    if (!container) return;

    const nextUrl = container.dataset.nextUrl;
    const sectionId = container.dataset.sectionId;
    if (!nextUrl || !sectionId) return;

    loading = true;
    setLoadingState(button, true);

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        throw new Error('Load More Socks: fetch failed with status ' + response.status);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Always look up the grid fresh too — same reasoning as the container:
      // a filter change may have replaced it since page load.
      const grid = document.querySelector('[data-load-more-grid]');
      const newGrid = doc.querySelector('[data-load-more-grid]');
      if (grid && newGrid) {
        const fragment = document.createDocumentFragment();
        Array.from(newGrid.children).forEach((card) => fragment.appendChild(card));
        grid.appendChild(fragment);

        document.dispatchEvent(
          new CustomEvent('boardroom:load-more-appended', { detail: { grid } })
        );
      }

      const newCount = doc.querySelector('[data-load-more-count]');
      const countEl = container.querySelector('[data-load-more-count]');
      if (newCount && countEl) {
        countEl.textContent = newCount.textContent;
      }

      const newContainer = doc.querySelector('[data-load-more-container]');
      const newNextUrl = newContainer ? newContainer.dataset.nextUrl : '';

      if (newNextUrl) {
        container.dataset.nextUrl = newNextUrl;
      } else {
        container.dataset.nextUrl = '';
        button.hidden = true;
      }
    } catch (err) {
      // Fail safe: leave the button clickable so the customer can just try again.
      console.error('Load More Socks error:', err);
    } finally {
      loading = false;
      setLoadingState(button, false);
    }
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-load-more-button]');
    if (button) {
      event.preventDefault();
      handleLoadMore(button);
    }
  });
})();